import { collections, firestoreHelpers, storageHelpers } from '@/lib/firebase';
import type { Driver, Vehicle, GeoPoint } from '@/types';

export const driverService = {
  // Create new driver
  async createDriver(userId: string, data: Partial<Driver>): Promise<void> {
    const driverData: Partial<Driver> = {
      ...data,
      type: 'driver',
      rating: 0,
      totalRides: 0,
      isOnline: false,
      isVerified: false,
      isActive: true,
      earnings: {
        today: 0,
        week: 0,
        month: 0,
        total: 0,
      },
      workHours: 0,
      acceptanceRate: 100,
      walletBalance: 0,
      documentsStatus: {
        license: 'pending',
        vehicle: 'pending',
        id: 'pending',
      },
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    
    await firestoreHelpers.createDocument(collections.drivers, driverData, userId);
  },

  // Get driver by ID
  async getDriver(userId: string): Promise<Driver | null> {
    return await firestoreHelpers.getDocument<Driver>(collections.drivers, userId);
  },

  // Update driver
  async updateDriver(userId: string, data: Partial<Driver>): Promise<void> {
    await firestoreHelpers.updateDocument(collections.drivers, userId, data);
  },

  // Subscribe to driver changes
  subscribeToDriver(userId: string, callback: (driver: Driver | null) => void) {
    return firestoreHelpers.subscribeToDocument<Driver>(
      collections.drivers,
      userId,
      callback
    );
  },

  // Update driver location
  async updateLocation(userId: string, lat: number, lng: number, heading?: number): Promise<void> {
    await this.updateDriver(userId, {
      currentLocation: { lat, lng },
      heading,
    });
  },

  // Set online/offline status
  async setOnlineStatus(userId: string, isOnline: boolean): Promise<void> {
    await this.updateDriver(userId, { isOnline });
  },

  // Upload driver photo
  async uploadDriverPhoto(userId: string, file: File, onProgress?: (progress: number) => void): Promise<string> {
    const path = `drivers/${userId}/profile.jpg`;
    const photoURL = await storageHelpers.uploadFile(file, path, onProgress);
    await this.updateDriver(userId, { photoURL });
    return photoURL;
  },

  // Upload vehicle photo
  async uploadVehiclePhoto(userId: string, file: File, onProgress?: (progress: number) => void): Promise<string> {
    const path = `drivers/${userId}/vehicle.jpg`;
    const photoURL = await storageHelpers.uploadFile(file, path, onProgress);
    await this.updateDriver(userId, { vehiclePhotoURL: photoURL });
    return photoURL;
  },

  // Upload license photo
  async uploadLicensePhoto(userId: string, file: File, onProgress?: (progress: number) => void): Promise<string> {
    const path = `drivers/${userId}/license.jpg`;
    const photoURL = await storageHelpers.uploadFile(file, path, onProgress);
    await this.updateDriver(userId, { 
      licensePhotoURL: photoURL,
      documentsStatus: { license: 'pending' }
    });
    return photoURL;
  },

  // Upload ID photo
  async uploadIdPhoto(userId: string, file: File, onProgress?: (progress: number) => void): Promise<string> {
    const path = `drivers/${userId}/id.jpg`;
    const photoURL = await storageHelpers.uploadFile(file, path, onProgress);
    await this.updateDriver(userId, { 
      idPhotoURL: photoURL,
      documentsStatus: { id: 'pending' }
    });
    return photoURL;
  },

  // Update vehicle info
  async updateVehicle(userId: string, vehicle: Vehicle): Promise<void> {
    await this.updateDriver(userId, { vehicle });
  },

  // Update earnings
  async addEarnings(userId: string, amount: number): Promise<void> {
    const driver = await this.getDriver(userId);
    if (driver && driver.earnings) {
      const earnings = driver.earnings;
      await this.updateDriver(userId, {
        earnings: {
          today: earnings.today + amount,
          week: earnings.week + amount,
          month: earnings.month + amount,
          total: earnings.total + amount,
        }
      });
    }
  },

  // Reset daily earnings
  async resetDailyEarnings(userId: string): Promise<void> {
    const driver = await this.getDriver(userId);
    if (driver && driver.earnings) {
      await this.updateDriver(userId, {
        earnings: {
          ...driver.earnings,
          today: 0,
        }
      });
    }
  },

  // Reset weekly earnings
  async resetWeeklyEarnings(userId: string): Promise<void> {
    const driver = await this.getDriver(userId);
    if (driver && driver.earnings) {
      await this.updateDriver(userId, {
        earnings: {
          ...driver.earnings,
          week: 0,
        }
      });
    }
  },

  // Reset monthly earnings
  async resetMonthlyEarnings(userId: string): Promise<void> {
    const driver = await this.getDriver(userId);
    if (driver && driver.earnings) {
      await this.updateDriver(userId, {
        earnings: {
          ...driver.earnings,
          month: 0,
        }
      });
    }
  },

  // Update wallet balance
  async updateWalletBalance(userId: string, amount: number): Promise<void> {
    const driver = await this.getDriver(userId);
    if (driver) {
      const newBalance = (driver.walletBalance || 0) + amount;
      await this.updateDriver(userId, { walletBalance: newBalance });
    }
  },

  // Update acceptance rate
  async updateAcceptanceRate(userId: string, accepted: boolean): Promise<void> {
    const driver = await this.getDriver(userId);
    if (driver) {
      // Simple calculation - in production, track total requests
      const currentRate = driver.acceptanceRate || 100;
      const newRate = accepted 
        ? Math.min(100, currentRate + 1) 
        : Math.max(0, currentRate - 5);
      await this.updateDriver(userId, { acceptanceRate: newRate });
    }
  },

  // Update work hours
  async addWorkHours(userId: string, hours: number): Promise<void> {
    const driver = await this.getDriver(userId);
    if (driver) {
      await this.updateDriver(userId, { 
        workHours: (driver.workHours || 0) + hours 
      });
    }
  },

  // Update rating
  async updateRating(userId: string, newRating: number): Promise<void> {
    const driver = await this.getDriver(userId);
    if (driver) {
      const totalRides = driver.totalRides || 1;
      const currentRating = driver.rating || 0;
      const updatedRating = ((currentRating * (totalRides - 1)) + newRating) / totalRides;
      await this.updateDriver(userId, { rating: Math.round(updatedRating * 10) / 10 });
    }
  },

  // Increment total rides
  async incrementTotalRides(userId: string): Promise<void> {
    const driver = await this.getDriver(userId);
    if (driver) {
      await this.updateDriver(userId, { 
        totalRides: (driver.totalRides || 0) + 1 
      });
    }
  },

  // Verify driver (admin only)
  async verifyDriver(userId: string): Promise<void> {
    await this.updateDriver(userId, { 
      isVerified: true,
      documentsStatus: {
        license: 'approved',
        vehicle: 'approved',
        id: 'approved',
      }
    });
  },

  // Reject driver documents (admin only)
  async rejectDocuments(userId: string, reason: string): Promise<void> {
    await this.updateDriver(userId, { 
      isVerified: false,
      documentsStatus: {
        license: 'rejected',
        vehicle: 'rejected',
        id: 'rejected',
      }
    });
  },

  // Get online drivers
  async getOnlineDrivers(): Promise<Driver[]> {
    return await firestoreHelpers.queryDocuments<Driver>(
      collections.drivers,
      [{ field: 'isOnline', operator: '==', value: true }],
      'rating',
      'desc'
    );
  },

  // Get nearby drivers
  async getNearbyDrivers(lat: number, lng: number, radiusKm: number = 5): Promise<Driver[]> {
    // In production, use GeoFirestore for efficient geo queries
    // For now, get all online drivers and filter client-side
    const onlineDrivers = await this.getOnlineDrivers();
    return onlineDrivers.filter(driver => {
      if (!driver.currentLocation) return false;
      const distance = calculateDistance(
        lat, lng,
        driver.currentLocation.lat,
        driver.currentLocation.lng
      );
      return distance <= radiusKm;
    });
  },

  // Get all drivers (for admin)
  async getAllDrivers(limitCount?: number): Promise<Driver[]> {
    return await firestoreHelpers.queryDocuments<Driver>(
      collections.drivers,
      [],
      'createdAt',
      'desc',
      limitCount
    );
  },

  // Get pending verifications (for admin)
  async getPendingVerifications(): Promise<Driver[]> {
    return await firestoreHelpers.queryDocuments<Driver>(
      collections.drivers,
      [{ field: 'isVerified', operator: '==', value: false }],
      'createdAt',
      'desc'
    );
  },

  // Search drivers (for admin)
  async searchDrivers(searchTerm: string): Promise<Driver[]> {
    const drivers = await this.getAllDrivers(100);
    return drivers.filter(d => 
      d.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.phone.includes(searchTerm) ||
      (d.vehicle?.plateNumber || '').includes(searchTerm)
    );
  },

  // Deactivate driver
  async deactivateDriver(userId: string): Promise<void> {
    await this.updateDriver(userId, { isActive: false, isOnline: false });
  },

  // Reactivate driver
  async reactivateDriver(userId: string): Promise<void> {
    await this.updateDriver(userId, { isActive: true });
  },

  // Update FCM token
  async updateFcmToken(userId: string, token: string): Promise<void> {
    await this.updateDriver(userId, { fcmToken: token });
  },

  // Subscribe to online drivers
  subscribeToOnlineDrivers(callback: (drivers: Driver[]) => void) {
    return firestoreHelpers.subscribeToQuery<Driver>(
      collections.drivers,
      [{ field: 'isOnline', operator: '==', value: true }],
      callback
    );
  }
};

// Helper function to calculate distance between two points
function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371; // Earth's radius in km
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * 
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(value: number): number {
  return value * Math.PI / 180;
}
